import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getCachedYoutubeVideos } from "../utils/homePageCache";
import axiosInstance from "../services/BaseService";

interface YoutubeVideoData {
  _id: string;
  title: string;
  description?: string;
  videoUrl: string;
  thumbnailUrl?: string;
  isSpecial: boolean;
  createdAt: string;
}

interface BasicHomeYoutubeSectionProps {
  useCached?: boolean;
}

const BasicHomeYoutubeSection: React.FC<BasicHomeYoutubeSectionProps> = ({ useCached = false }) => {
  const [videos, setVideos] = useState<YoutubeVideoData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    try {
      setLoading(true);
      
      if (useCached) {
        // Use cached version
        const data = await getCachedYoutubeVideos(4, false);
        setVideos(data.videos || []);
      } else {
        // Use direct API call
        const response = await axiosInstance.get('/youtube-videos', {
          params: {
            limit: 4,
            isActive: true,
            isSpecial: false
          }
        });
        
        const data = response.data;
        setVideos(data.videos || []);
        
        // Store in cache for future use
        const { cacheSet } = await import('../utils/cacheUtil');
        await cacheSet(`youtube_videos_4_false`, data, 21600); // 6 hours TTL
      }
    } catch (error) {
      console.error("Failed to load videos:", error);
    } finally {
      setLoading(false);
    }
  };

  // Extract YouTube video ID from URL
  const getYoutubeVideoId = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  return (
    <section className="py-5" style={{ backgroundColor: "#f8f9fa" }}>
      <Container>
        <div className="text-center mb-4">
          <h2 className="mb-3" style={{ color: "#810081" }}>
            <i className="bi bi-youtube me-2"></i>
            Video Gallery
          </h2>
          <div 
            className="mx-auto mb-4" 
            style={{ 
              width: "80px", 
              height: "3px", 
              background: "linear-gradient(45deg, #810081, #FFD600)", 
              borderRadius: "2px" 
            }}
          ></div>
        </div>
        
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" role="status" style={{ color: "#810081" }}>
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : videos.length > 0 ? (
          <>
            <Row className="g-4">
              {videos.map((video) => {
                const videoId = getYoutubeVideoId(video.videoUrl);
                return (
                  <Col key={video._id} lg={3} md={6} className="mb-4">
                    <Card 
                      className="h-100 shadow-sm video-card"
                      style={{ 
                        cursor: "pointer",
                        transition: "transform 0.3s ease, box-shadow 0.3s ease",
                        borderRadius: "15px",
                        overflow: "hidden"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-5px)";
                        e.currentTarget.style.boxShadow = "0 10px 20px rgba(129, 0, 129, 0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 4px 6px rgba(0,0,0,0.1)";
                      }}
                    >
                      <a 
                        href={`https://www.youtube.com/watch?v=${videoId}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-decoration-none"
                      >
                        <div className="position-relative">
                          <Card.Img 
                            variant="top" 
                            src={video.thumbnailUrl || `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                            alt={video.title}
                            style={{ height: "180px", objectFit: "cover" }}
                          />
                          <div 
                            className="position-absolute top-50 start-50 translate-middle"
                            style={{ color: 'white', fontSize: '3rem', opacity: 0.8 }}
                          >
                            <i className="bi bi-play-circle-fill"></i>
                          </div>
                        </div>
                        <Card.Body>
                          <Card.Title className="h6" style={{ color: "#810081" }}>{video.title}</Card.Title>
                          {video.description && (
                            <Card.Text className="small text-muted">
                              {video.description.length > 80 
                                ? `${video.description.substring(0, 80)}...` 
                                : video.description}
                            </Card.Text>
                          )}
                        </Card.Body>
                      </a>
                    </Card>
                  </Col>
                );
              })}
            </Row>
            
            <div className="text-center mt-4">
              <Link 
                to="/videos" 
                className="btn px-4 py-2"
                style={{ 
                  backgroundColor: "#810081", 
                  color: "white", 
                  borderRadius: "30px",
                  fontWeight: "500"
                }}
              >
                View All Videos <i className="bi bi-arrow-right ms-2"></i>
              </Link>
            </div>
          </>
        ) : (
          <div className="text-center py-5">
            <i className="bi bi-film display-1 text-muted"></i>
            <h4 className="mt-3 text-muted">No videos available</h4>
          </div>
        )}
      </Container>
    </section>
  );
};

export default BasicHomeYoutubeSection;